<template>
	<div class="flex justify-between">
		<div class="-mx-2 -my-2 flex flex-wrap lg:w-11/12">
			<template
				v-for="(
					removableFilter, removableFilterKey
				) in removableFilters"
				:key="removableFilterKey"
			>
				<template v-if="Array.isArray(removableFilter)">
					<div
						v-for="(removableFilterItem, key) in removableFilter"
						:key="key"
						class="px-2 py-2"
					>
						<RemoveFilterButton
							v-if="removableFilterItem"
							:is-disabled="isFiltering"
							@click="
								removeFilter(
									removableFilterKey,
									removableFilterItem.value
								)
							"
						>
							{{ removableFilterItem.label }}
						</RemoveFilterButton>
					</div>
				</template>

				<!--            TODO try to keep the below commented for any future cases by normalizing removableFilter to be an array -->

				<!--            <template v-else>-->
				<!--                <remove-filter-button v-bind="$attrs"-->
				<!--                                      :class="[]"-->
				<!--                                      @click="removeFilter(removableFilterKey)"-->
				<!--                >-->
				<!--                    {{ removableFilter }}-->
				<!--                </remove-filter-button>-->
				<!--            </template>-->
			</template>

			<!--        {{ removableFilters }}-->
		</div>

		<div class="flex items-center justify-end lg:w-1/12">
			<span
				v-if="Object.keys(removableFilters).length"
				class="mt-2 text-sm underline underline-offset-2"
				:class="
					isFiltering
						? 'cursor-default'
						: 'cursor-pointer hover:text-blue-400'
				"
				@click="onClearAll"
			>
				Clear all
			</span>
		</div>
	</div>
</template>

<script setup>
import RemoveFilterButton from '@/components/buttons/RemoveFilterButton.vue'
import { useProductCatalog } from '@/stores/product-catalog'
import {
	cloneDeep as _cloneDeep,
	find as _find,
	has as _has,
	isEmpty as _isEmpty,
} from 'lodash-es'

const productCatalog = useProductCatalog()

const emits = defineEmits(['click'])

const facets = computed(() => productCatalog.facets)

const activeFilters = computed(() => productCatalog.activeFilters)

const isFiltering = computed(() => productCatalog.isFiltering)

const removableFilters = computed(() => {
	const currentActiveFilters = _cloneDeep(activeFilters.value)
	const currentFacets = _cloneDeep(facets.value)
	const filtersThatAreRemovable = {}

	Object.entries(currentActiveFilters).forEach(
		([activeFilterKey, activeFilter]) => {
			if (activeFilterKey === 'sortBy') {
				return
			}

			if (
				activeFilterKey === 'priceFrom' ||
				activeFilterKey === 'priceTo'
			) {
				const priceFrom =
					currentActiveFilters.priceFrom &&
					currentActiveFilters.priceFrom !== 'all'
						? `$${currentActiveFilters.priceFrom}pw`
						: 'Any'
				const priceTo =
					currentActiveFilters.priceTo &&
					currentActiveFilters.priceTo !== 'all'
						? `$${currentActiveFilters.priceTo}pw`
						: 'Any'
				filtersThatAreRemovable.price = [
					{
						label: `${priceFrom} - ${priceTo}`,
					},
				]
				return
			}

			if (
				activeFilterKey === 'priceUsedFrom' ||
				activeFilterKey === 'priceUsedTo'
			) {
				const priceUsedFrom =
					currentActiveFilters.priceUsedFrom &&
					currentActiveFilters.priceUsedFrom !== 'all'
						? `$${currentActiveFilters.priceUsedFrom}`
						: 'Any'
				const priceUsedTo =
					currentActiveFilters.priceUsedTo &&
					currentActiveFilters.priceUsedTo !== 'all'
						? `$${currentActiveFilters.priceUsedTo}`
						: 'Any'
				filtersThatAreRemovable.price = [
					{
						label: `${priceUsedFrom} - ${priceUsedTo}`,
					},
				]
				return
			}

			if (
				activeFilterKey === 'yearFrom' ||
				activeFilterKey === 'yearTo'
			) {
				const yearFrom =
					currentActiveFilters.yearFrom &&
					currentActiveFilters.yearFrom !== 'all'
						? `${currentActiveFilters.yearFrom}`
						: 'Any'
				const yearTo =
					currentActiveFilters.yearTo &&
					currentActiveFilters.yearTo !== 'all'
						? `${currentActiveFilters.yearTo}`
						: 'Any'
				filtersThatAreRemovable.year = [
					{
						label: `${yearFrom} - ${yearTo}`,
					},
				]
				return
			}

			if (
				activeFilterKey === 'odometerFrom' ||
				activeFilterKey === 'odometerTo'
			) {
				const odometerFrom =
					currentActiveFilters.odometerFrom &&
					currentActiveFilters.odometerFrom !== 'all'
						? `${currentActiveFilters.odometerFrom}km`
						: 'Any'
				const odometerTo =
					currentActiveFilters.odometerTo &&
					currentActiveFilters.odometerTo !== 'all'
						? `${currentActiveFilters.odometerTo}km`
						: 'Any'
				filtersThatAreRemovable.odometer = [
					{
						label: `${odometerFrom} - ${odometerTo}`,
					},
				]
				return
			}

			filtersThatAreRemovable[activeFilterKey] = []

			const activeFilterAsArray = Array.isArray(activeFilter)
				? activeFilter
				: [activeFilter]

			activeFilterAsArray.forEach((activeFilterItem) => {
				if (`${activeFilterItem}`.toLowerCase() === 'all') {
					return
				}

				// TODO use "groups": true, to check if it's grouped
				// note that this 'groups' approach is not being utilized
				const isGroupedItems =
					!_isEmpty(currentFacets[activeFilterKey]) &&
					_has(currentFacets[activeFilterKey][0], 'options')
				// let isGroupedItems = false
				// if(currentFacets[activeFilterKey] && Array.isArray(currentFacets[activeFilterKey]) &&
				//     currentFacets[activeFilterKey][0].options) {
				// }
				const facetItems = isGroupedItems
					? []
					: currentFacets[activeFilterKey]
				if (isGroupedItems) {
					currentFacets[activeFilterKey].forEach((facetItem) => {
						facetItems.push(...facetItem.options)
					})
				}

				const removableFacetItem = _find(
					facetItems,
					(facetItem) => facetItem.value === activeFilterItem
				)

				if (!removableFacetItem) {
					return
				}

				filtersThatAreRemovable[activeFilterKey].push(
					removableFacetItem
				)
			})
		}
	)

	return filtersThatAreRemovable
})

const removeFilter = async (filterKey, filterValue = null) => {
	if (isFiltering.value) return

	if (filterKey === 'price') {
		removePriceFilter()
		return
	}

	if (filterKey === 'year') {
		removeYearFilter()
		return
	}

	if (filterKey === 'odometer') {
		removeOdometerFilter()
		return
	}

	productCatalog.removeFilter({
		filterKey,
		filterValue,
	})

	await productCatalog.updateProductsPagination({
		filterNextPage: 1,
	})
}

const removePriceFilter = () => {
	productCatalog.removeFilter([
		'priceFrom',
		'priceTo',
		'priceUsedFrom',
		'priceUsedTo',
	])
}

const removeYearFilter = () => {
	productCatalog.removeFilter(['yearFrom', 'yearTo'])
}

const removeOdometerFilter = () => {
	productCatalog.removeFilter(['odometerFrom', 'odometerTo'])
}

const onClearAll = () => {
	if (isFiltering.value) return

	productCatalog.clearFilter()
}
</script>
